import ActionButton from '@/components/Button/ActionButton';
import IconButton from '@/components/Button/IconButton';
import { InputEmail } from '@/components/Input/Email/InputEmail';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import type { EmailListItemValue } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

type Props = CFormItemProps & {
  value: EmailListItemValue[];
  onChange: (value: EmailListItemValue[]) => void;
  addButtonLabel?: string;
};

/**
 * メールアドレスの配列入力。
 * 編集時に行の追加・削除ができる。
 * メイン/サブの区別は属性分割で行うため、このコンポーネントでは isPrimary の UI 制御は行わない。
 */
export const CFormEmailListItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const values = props.value ?? [];

  const handleChange = (index: number, newEmail: string) => {
    const next = values.map((v, i) =>
      i === index ? { ...v, email: newEmail } : v,
    );
    props.onChange(next);
  };

  const handleAdd = () => {
    props.onChange([...values, { email: '', isPrimary: false }]);
  };

  const handleRemove = (index: number) => {
    props.onChange(values.filter((_, i) => i !== index));
  };

  const validValues = values.filter((v) => v.email.trim() !== '');

  const readComponent = (
    <VStack className="gap-2">
      {validValues.length === 0 ? (
        <Text variant="pSmall" className="text-neutral-muted-on-fill">
          -
        </Text>
      ) : (
        validValues.map((v, index) => (
          <Text key={`read-${index}`} variant="labelLarge">
            {v.email}
          </Text>
        ))
      )}
    </VStack>
  );

  const writeComponent = (
    <VStack className="gap-3">
      {values.map((v, index) => (
        <HStack key={`email-${index}`} className="gap-2 items-center">
          <InputEmail
            value={v.email}
            onChange={(value) => handleChange(index, value)}
          />
          {!props.readonly && (
            <IconButton
              icon="Trash2Icon"
              variant="ghost"
              size="Small"
              className="shrink-0"
              onClick={() => handleRemove(index)}
              aria-label={tc('action.delete')}
            />
          )}
        </HStack>
      ))}
      {!props.readonly && (
        <ActionButton
          icon="PlusIcon"
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={handleAdd}
        >
          {props.addButtonLabel ?? tc('action.add')}
        </ActionButton>
      )}
    </VStack>
  );

  return (
    <CFormItemBase
      {...props}
      readComponent={readComponent}
      writeComponent={writeComponent}
    />
  );
});
